import { StepLayout } from '@/components/NewGeneralDialog/StepLayout';
import { SectionHeader } from '@/components/ReviewSteps/SectionHeader';
import { SectionTable } from '@/components/ReviewSteps/SectionTable';
import { StepHeader } from '@/components/ReviewSteps/StepHeader';
import { WizardAnimationWrapper } from '@/components/WizardAnimationWrapper';
import { ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS } from '@/constants/documents';
import { Documents } from '@/types/company';
import { Button, Stack } from '@mui/material';
import {
  formatCurrency,
  parseLocalizedNumber,
} from '@treyd-io/core/utils/number';
import ArrowRightIcon from '@treyd-io/treyd-ui/icons/ArrowRightIcon';
import { toString } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PdfPreviewStep } from './PdfPreviewStep';

interface ReviewStepProps {
  formData: Partial<Documents.DocumentType['data']>;
  documentId: number;
  isLoading: boolean;
  onSubmit: () => void;
  onMovingBackward: () => void;
  onMovingForward: () => void;
  onEditClick: (stepName: ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS) => void;
  next?: boolean;
}

export const ReviewStep = ({
  formData,
  documentId,
  isLoading,
  onSubmit,
  onMovingBackward,
  onMovingForward,
  onEditClick,
  next,
}: ReviewStepProps) => {
  const { t } = useTranslation();
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  const assetsCurrency = toString(formData?.Assets_Currency) || '';
  const liabilitiesCurrency = toString(formData?.Liabilities_Currency) || '';
  const incomeCurrency = toString(formData?.Income_Currency) || '';

  // Format values for display
  const formatValue = (
    value: string | number | null | undefined,
    currency: string
  ) => {
    if (!value) return '-';
    if (typeof value === 'string' && value.endsWith('%')) return value;
    return formatCurrency(parseLocalizedNumber(value), currency);
  };

  if (showPdfPreview) {
    return (
      <PdfPreviewStep
        isGeneratingDocument={isLoading}
        documentId={documentId}
        onMovingForward={onMovingForward}
      />
    );
  }

  return (
    <WizardAnimationWrapper next={next}>
      <StepLayout
        onMovingBackward={onMovingBackward}
        containerProps={{
          gap: 0.5,
        }}>
        <StepHeader
          title={t('review_your_information')}
          descriptions={[
            t(
              'please_take_a_moment_to_verify_the_information_youve_provided_you_may_still_make_edits_before_the_final_document_is_generated'
            ),
          ]}
        />
        <Stack gap={3} marginTop={2.5}>
          <Stack gap={2}>
            <SectionHeader
              title={t('personal_details')}
              onEditClick={() =>
                onEditClick(
                  ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS[
                    'Statement of assets & liabilities'
                  ]
                )
              }
            />
            <SectionTable
              rows={[
                { key: t('date_of_birth'), value: formData?.Signer_Birthdate },
                { key: t('address'), value: formData?.Signer_Address },
              ]}
            />
          </Stack>
          <Stack gap={2}>
            <SectionHeader
              title={t('assets')}
              onEditClick={() =>
                onEditClick(ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS.Assets)
              }
            />
            <SectionTable
              rows={[
                {
                  key: t('primary_home'),
                  value: formatValue(formData?.Primary_Home, assetsCurrency),
                },
                {
                  key: t('home_ownership'),
                  value: `${formData?.Home_Ownership}%`,
                },
                {
                  key: t('investment_properties'),
                  value: formatValue(
                    formData?.Investment_Properties,
                    assetsCurrency
                  ),
                },
                {
                  key: t('cash_term_deposits'),
                  value: formatValue(formData?.Cash, assetsCurrency),
                },
                {
                  key: t('own_shares'),
                  value: formatValue(formData?.Own_Shares, assetsCurrency),
                },
                {
                  key: t('listed_shares'),
                  value: formatValue(formData?.Listed_Shares, assetsCurrency),
                },
                {
                  key: t('pension_funds'),
                  value: formatValue(formData?.Pension, assetsCurrency),
                },
                {
                  key: t('vehicles_and_other_assets'),
                  value: formatValue(formData?.Vehicles, assetsCurrency),
                },
              ]}
            />
          </Stack>
          <Stack gap={2}>
            <SectionHeader
              title={t('liabilities')}
              onEditClick={() =>
                onEditClick(
                  ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS.Liabilities
                )
              }
            />
            <SectionTable
              rows={[
                {
                  key: t('home_loan'),
                  value: formatValue(formData?.Home_Loan, liabilitiesCurrency),
                },
                {
                  key: t('investment_loans'),
                  value: formatValue(
                    formData?.Investment_Loans,
                    liabilitiesCurrency
                  ),
                },
                {
                  key: t('auto_loans'),
                  value: formatValue(formData?.Auto_Loans, liabilitiesCurrency),
                },
                {
                  key: t('other_loans'),
                  value: formatValue(
                    formData?.Other_Loans,
                    liabilitiesCurrency
                  ),
                },
                {
                  key: t('credit_cards'),
                  value: formatValue(
                    formData?.Credit_cards,
                    liabilitiesCurrency
                  ),
                },
              ]}
            />
          </Stack>
          <Stack gap={2}>
            <SectionHeader
              title={t('income_information')}
              onEditClick={() =>
                onEditClick(
                  ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS[
                    'Income information'
                  ]
                )
              }
            />
            <SectionTable
              rows={[
                { key: t('income_year'), value: formData?.Income_Year },
                {
                  key: t('annual_income_before_tax_last_year'),
                  value: formatValue(formData?.Income_Amount, incomeCurrency),
                },
              ]}
            />
          </Stack>

          <Button
            size="large"
            variant="contained"
            onClick={() => {
              onSubmit();
              setShowPdfPreview(true);
            }}
            loading={isLoading}
            endIcon={<ArrowRightIcon />}
            sx={{ alignSelf: { sm: 'flex-start' }, marginTop: 0.5 }}>
            {t('review_and_sign')}
          </Button>
        </Stack>
      </StepLayout>
    </WizardAnimationWrapper>
  );
};
